
import { computed, defineComponent, PropType, ref, watch } from '@vue/composition-api'

import { resolveCountry } from '@/plugins/i18n'

import { TranslationGroup, useUpdateTranslation } from '@/api/translations'
import { Rights } from '@/api/rights'
import { useNotify, useAuthGetters } from '@/store'

import { debounce } from 'lodash-es'
import { mdiAlertOutline, mdiEarth } from '@mdi/js'

import CommonFlagIcon from '@/components/common/common-flag-icon.vue'

export default defineComponent({
  name: 'translation-item',
  components: {
    CommonFlagIcon,
  },
  props: {
    displayLocales: {
      type: Array as PropType<string[]>,
      required: true,
    },
    translationGroup: {
      type: Object as PropType<TranslationGroup>,
      reqired: true,
      default: () => ({
        key: '',
        translations: [],
      }),
    },
  },
  setup: (props, { root }) => {
    const { hasRight } = useAuthGetters()
    const hasTranslationUpdateRight = hasRight.value(Rights.TRANSLATION_UPDATE)

    const { updateTranslation, isLoading } = useUpdateTranslation()

    const { addNotification } = useNotify()

    const translationInputs = ref<{ [key: string]: string }>({})

    const implementedLocales = computed<string[]>(() => root.$store.state.locale.implementedLocales)

    watch(
      implementedLocales,
      (val) => {
        for (const locale of val) {
          translationInputs.value[locale] =
            props.translationGroup.translations.find((translation) => translation.locale === locale)?.value || ''
        }
      },
      { immediate: true }
    )

    const missingError = ref('')

    const updateError = debounce(
      () => {
        const translations = Object.entries(translationInputs.value)
          .filter(([key]) => props.displayLocales.includes(key))
          .map(([, value]) => value)

        missingError.value = translations.every((translation) => !translation.trim().length)
          ? 'error'
          : translations.some((translation) => !translation.trim().length)
          ? 'warning'
          : ''
      },
      1000,
      { leading: true }
    )

    watch(
      () => props.displayLocales,
      () => updateError(),
      { immediate: true }
    )

    const updateTranslations = () => {
      const translationsToUpdate = Object.entries(translationInputs.value).filter(
        ([key, value]) => props.displayLocales.includes(key) && value.trim()
      )

      const promises = translationsToUpdate.map(([key, value]) =>
        updateTranslation({ key: props.translationGroup.key, value: value, locale: key })
      )

      Promise.all(promises)
        .then(() => {
          addNotification({
            type: 'success',
            text: root.$t('translations.update.success') as string,
          })
        })
        .catch((error) => {
          error.userMessage = root.$t('translations.update.error')

          throw error
        })
    }

    return {
      icons: {
        mdiAlertOutline,
        mdiEarth,
      },
      isLoading,
      translationInputs,
      missingError,
      updateError,
      updateTranslations,
      resolveCountry,
      hasTranslationUpdateRight,
    }
  },
})
